import { useI18n } from "@/context/I18nContext";
import { LOCALES, type SupportedLocale } from "@chinese-vendor/i18n";

// Derive supported locale list from the shared LOCALES map to avoid duplication.
export const SUPPORTED_LOCALES: { locale: SupportedLocale; nativeLabel: string }[] = (
  Object.keys(LOCALES) as SupportedLocale[]
).map((locale) => ({
  locale,
  nativeLabel: LOCALES[locale],
}));

/**
 * Maps each supported locale to its corresponding Tailwind font-family token.
 *
 * Token → font stack (defined in tailwind.config.js):
 *   font-sans    → Inter Display (English and Chinese Simplified)
 *   font-saudi   → Saudi (Arabic)
 */
const LOCALE_FONT_CLASS: Record<SupportedLocale, string> = {
  en: "font-sans",
  "zh-CN": "font-sans",
  ar: "font-saudi",
};

/**
 * Returns the Tailwind font-family class for the currently active locale.
 * Apply this class to root text containers so the correct typeface renders
 * automatically for English, Chinese, and Arabic users.
 *
 * Prefer using {@link AppText} (from `@/components/ui/AppText`) in components —
 * it calls this hook internally. Use `useLocaleFont()` only when building a
 * custom text primitive that cannot extend `AppText`.
 *
 * @example
 * // In a custom primitive (not a standard screen or UI component):
 * const fontClass = useLocaleFont(); // "font-sans" | "font-saudi"
 * <AppText className={`text-base ${fontClass}`}>…</AppText>
 */
export function useLocaleFont(): string {
  const { locale } = useI18n();
  return LOCALE_FONT_CLASS[locale] ?? "font-sans";
}

/**
 * Convenience hook wrapping the i18n context.
 * Use when you only need locale management without the full t() function.
 */
export function useLanguage() {
  const { locale, setLocale, isRTL: rtl } = useI18n();

  return {
    locale,
    setLocale,
    isRTL: rtl,
    availableLocales: SUPPORTED_LOCALES,
    /** Tailwind class for the active locale's font family */
    fontClass: LOCALE_FONT_CLASS[locale] ?? "font-sans",
  };
}
